import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { seasonalThemeAPI } from '@/lib/api';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

/**
 * Light-only base scheme + a central Seasonal Theme layer.
 *
 * The base product is light-only (dark mode intentionally disabled). On top
 * of that, this provider applies the single ACTIVE seasonal theme by writing
 * its design-token overrides onto `document.documentElement`. Because the
 * whole portal is styled from those CSS variables, changing them here
 * re-skins every page at once — there is no parallel styling system.
 *
 * It also reflects the active theme's decorative effect through a
 * `data-season-effect` attribute consumed by <SeasonalAccents/>.
 */
export const ThemeProvider = ({ children }) => {
  const [activeSeason, setActiveSeason] = useState(null);
  const appliedKeys = useRef([]);

  // Base: force light, wipe any legacy dark preference.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    try { localStorage.setItem('kr-theme', 'light'); } catch { /* ignore */ }
  }, []);

  const clearSeasonTokens = useCallback(() => {
    const root = document.documentElement;
    appliedKeys.current.forEach((k) => root.style.removeProperty(`--${k}`));
    appliedKeys.current = [];
    root.removeAttribute('data-season');
    root.setAttribute('data-season-effect', 'none');
  }, []);

  const applySeason = useCallback((theme) => {
    const root = document.documentElement;
    // Remove any previously applied overrides first so switching themes
    // never leaves stale variables behind.
    appliedKeys.current.forEach((k) => root.style.removeProperty(`--${k}`));
    appliedKeys.current = [];
    if (!theme || !theme.tokens) {
      root.removeAttribute('data-season');
      root.setAttribute('data-season-effect', 'none');
      return;
    }
    Object.entries(theme.tokens).forEach(([k, v]) => {
      if (typeof v === 'string' && v.trim()) {
        root.style.setProperty(`--${k}`, v);
        appliedKeys.current.push(k);
      }
    });
    root.setAttribute('data-season', theme.template || 'custom');
    root.setAttribute('data-season-effect', theme.accent_effect || 'none');
  }, []);

  const refreshSeason = useCallback(async () => {
    try {
      const { data } = await seasonalThemeAPI.active();
      setActiveSeason(data || null);
      applySeason(data || null);
    } catch {
      setActiveSeason(null);
      applySeason(null);
    }
  }, [applySeason]);

  useEffect(() => {
    refreshSeason();
    // Mobile/tablet PWAs keep the app alive in the background, so a theme
    // activated elsewhere was never picked up. Re-check whenever the app
    // comes back to the foreground (and on a slow heartbeat as a fallback).
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshSeason();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refreshSeason);
    const heartbeat = setInterval(refreshSeason, 5 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshSeason);
      clearInterval(heartbeat);
    };
  }, [refreshSeason]);

  const value = {
    theme: 'light',
    setTheme: () => { /* light-only: no-op */ },
    toggleTheme: () => { /* light-only: no-op */ },
    activeSeason,
    refreshSeason,
    // Live in-page preview helpers for the admin editor.
    previewSeason: applySeason,
    clearSeasonPreview: () => applySeason(activeSeason),
    clearSeasonTokens,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
